import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface SmartLinkProps {
  href: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
  'aria-label'?: string;
  title?: string;
}

/**
 * Renders a router <Link> for internal paths ("/about", "/slug#anchor") so
 * navigation stays client-side, and a plain <a> for hash-only, mailto and
 * external (http) links - opening external links in a new tab.
 */
export function SmartLink({ href, className, children, onClick, ...rest }: SmartLinkProps) {
  const isInternal = href.startsWith('/') && !href.startsWith('//');

  if (isInternal) {
    return (
      <Link to={href} className={className} onClick={onClick} {...rest}>
        {children}
      </Link>
    );
  }

  const isExternal = /^https?:/i.test(href);
  return (
    <a
      href={href}
      className={className}
      onClick={onClick}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      {...rest}
    >
      {children}
    </a>
  );
}
